import type { CognitoUserSession } from "amazon-cognito-identity-js";
import QRCode from "qrcode";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { listFiles, resolveShare, revokeFile, uploadFile, type DropJetFile } from "./api";
import { confirmSignUp, currentSession, signIn, signOut, signUp } from "./auth";
import { config } from "./config";
import {
  errorMessage,
  formatBytes,
  getShareToken,
  saveShareToken,
  shareUrl,
} from "./utils";

export function App() {
  const downloadMatch = window.location.pathname.match(/^\/d\/([^/]+)$/);
  if (downloadMatch?.[1]) return <DownloadPage fileId={decodeURIComponent(downloadMatch[1])} />;
  return <MainApp />;
}

function MainApp() {
  const [session, setSession] = useState<CognitoUserSession | null | undefined>(undefined);
  useEffect(() => void currentSession().then(setSession), []);

  if (session === undefined) return <Shell><div className="loader">Preparing launch pad…</div></Shell>;
  if (!session) return <Shell><AuthPanel onAuthenticated={setSession} /></Shell>;

  const email = String(session.getIdToken().payload.email ?? "pilot");
  return (
    <Shell
      user={email}
      onSignOut={() => {
        signOut();
        setSession(null);
      }}
    >
      <Dashboard session={session} />
    </Shell>
  );
}

function Shell({
  children,
  user,
  onSignOut,
}: {
  children: React.ReactNode;
  user?: string;
  onSignOut?: () => void;
}) {
  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="DropJet home">
          <span className="brand-mark">DJ</span>
          <span>DropJet</span>
        </a>
        {user && (
          <div className="user-actions">
            <span className="user-email">{user}</span>
            <button className="button ghost compact" onClick={onSignOut}>Sign out</button>
          </div>
        )}
      </header>
      <main>{children}</main>
      <footer>Private by default · Temporary by design · Built serverlessly on AWS <br/> Coded with love by <a href={"https://mendisofficial.me"} target={"_blank"}>Chathusha Mendis</a></footer>
    </div>
  );
}

function AuthPanel({ onAuthenticated }: { onAuthenticated: (session: CognitoUserSession) => void }) {
  const [mode, setMode] = useState<"signin" | "signup" | "confirm">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "signup") {
        await signUp(email, password);
        setMode("confirm");
      } else if (mode === "confirm") {
        await confirmSignUp(email, code);
        onAuthenticated(await signIn(email, password));
      } else {
        onAuthenticated(await signIn(email, password));
      }
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="hero auth-layout">
      <div className="hero-copy">
        <p className="eyebrow">Files with an exit plan</p>
        <h1>Send it.<br /><span>Then let it disappear.</span></h1>
        <p className="lede">
          DropJet creates private, expiring links without routing your file through an application server.
        </p>
        <div className="feature-row">
          <span>✦ Direct upload</span><span>✦ Timed expiry</span><span>✦ Revocable links</span>
        </div>
      </div>
      <form className="card auth-card" onSubmit={submit}>
        <p className="eyebrow">{mode === "signin" ? "Welcome back" : mode === "signup" ? "Create an account" : "Check your inbox"}</p>
        <h2>{mode === "signin" ? "Enter the cockpit" : mode === "signup" ? "Start sharing" : "Confirm your email"}</h2>
        <label>Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={mode === "confirm"} /></label>
        {mode !== "confirm" && <label>Password<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={10} required /></label>}
        {mode === "confirm" && <label>Confirmation code<input inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus /></label>}
        {error && <p className="notice error">{error}</p>}
        <button className="button primary" disabled={busy}>{busy ? "Working…" : mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Confirm & sign in"}</button>
        {mode !== "confirm" && (
          <button className="text-button" type="button" onClick={() => setMode(mode === "signin" ? "signup" : "signin")}>
            {mode === "signin" ? "New here? Create an account" : "Already registered? Sign in"}
          </button>
        )}
      </form>
    </section>
  );
}

function Dashboard({ session }: { session: CognitoUserSession }) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [expiry, setExpiry] = useState(24);
  const [downloadLimit, setDownloadLimit] = useState("");
  const [files, setFiles] = useState<DropJetFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [error, setError] = useState("");
  const [latestShare, setLatestShare] = useState("");
  const [qrCode, setQrCode] = useState("");

  async function refresh() {
    setLoadingFiles(true);
    try {
      setFiles(await listFiles(session));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoadingFiles(false);
    }
  }
  useEffect(() => void refresh(), [session]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selectedFile) return;
    setBusy(true);
    setError("");
    setLatestShare("");
    try {
      const result = await uploadFile(
        session,
        selectedFile,
        expiry,
        downloadLimit ? Number(downloadLimit) : undefined,
      );
      saveShareToken(result.file.fileId, result.shareToken);
      const url = shareUrl(result.file.fileId, result.shareToken);
      setLatestShare(url);
      setQrCode(await QRCode.toDataURL(url, { width: 280, margin: 2, color: { dark: "#08131fff", light: "#ffffffff" } }));
      setSelectedFile(null);
      await refresh();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(fileId: string) {
    if (!window.confirm("Revoke this link and delete its file now?")) return;
    try {
      await revokeFile(session, fileId);
      await refresh();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <div className="dashboard">
      <section className="dashboard-heading">
        <div><p className="eyebrow">Launch pad</p><h1>Send a file into the cloud.</h1></div>
        <div className="privacy-pill"><span>●</span> Private S3 storage</div>
      </section>

      <form className="card upload-card" onSubmit={submit}>
        <label className="drop-zone">
          <input type="file" onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)} />
          <span className="upload-icon">↑</span>
          {selectedFile ? (
            <><strong>{selectedFile.name}</strong><small>{formatBytes(selectedFile.size)}</small></>
          ) : (
            <><strong>Choose a file to launch</strong><small>Up to {formatBytes(config().maxFileSizeBytes)} · uploaded directly to S3</small></>
          )}
        </label>
        <div className="upload-options">
          <label>Expires after<select value={expiry} onChange={(e) => setExpiry(Number(e.target.value))}>{config().allowedExpiryHours.map((hours) => <option value={hours} key={hours}>{hours} hour{hours === 1 ? "" : "s"}</option>)}</select></label>
          <label>Download limit <span>(optional)</span><input type="number" min="1" max="100" placeholder="Unlimited" value={downloadLimit} onChange={(e) => setDownloadLimit(e.target.value)} /></label>
          <button className="button primary launch" disabled={!selectedFile || busy || Boolean(selectedFile && selectedFile.size > config().maxFileSizeBytes)}>{busy ? "Launching…" : "Launch file →"}</button>
        </div>
        {selectedFile && selectedFile.size > config().maxFileSizeBytes && <p className="notice error">That file exceeds the configured size limit.</p>}
        {error && <p className="notice error">{error}</p>}
      </form>

      {latestShare && (
        <section className="card share-result">
          <div><p className="eyebrow success">Ready to share</p><h2>Your private link is live</h2><p>The secret stays after the <code>#</code>, so browsers do not send it in HTTP request logs.</p></div>
          {qrCode && <img src={qrCode} alt="QR code for the new DropJet link" />}
          <div className="copy-row"><input value={latestShare} readOnly /><button className="button secondary" onClick={() => void navigator.clipboard.writeText(latestShare)}>Copy link</button></div>
        </section>
      )}

      <section className="files-section">
        <div className="section-title"><div><p className="eyebrow">Flight log</p><h2>Your recent drops</h2></div><button className="button ghost compact" onClick={() => void refresh()}>Refresh</button></div>
        {loadingFiles ? <div className="empty">Loading flights…</div> : files.length === 0 ? <div className="empty">No files yet. Your first launch will appear here.</div> : (
          <div className="file-list">{files.map((file) => <FileRow file={file} key={file.fileId} onRevoke={() => void revoke(file.fileId)} />)}</div>
        )}
      </section>
    </div>
  );
}

function FileRow({ file, onRevoke }: { file: DropJetFile; onRevoke: () => void }) {
  const token = getShareToken(file.fileId);
  const url = token ? shareUrl(file.fileId, token) : null;
  const isExpired = file.expiresAt <= Date.now() / 1000;
  return (
    <article className="file-row">
      <div className="file-symbol">{file.originalName.split(".").pop()?.slice(0, 3).toUpperCase() ?? "FILE"}</div>
      <div className="file-main"><strong>{file.originalName}</strong><span>{formatBytes(file.size)} · {file.downloads} access{file.downloads === 1 ? "" : "es"}</span></div>
      <div className={`status ${file.status.toLowerCase()}`}>{isExpired ? "EXPIRED" : file.status}</div>
      <div className="file-expiry">{isExpired ? "Expired" : `Expires ${new Date(file.expiresAt * 1000).toLocaleString()}`}</div>
      <div className="file-actions">{url && file.status === "AVAILABLE" && !isExpired && <button className="button ghost compact" onClick={() => void navigator.clipboard.writeText(url)}>Copy</button>}<button className="button danger compact" disabled={file.status === "REVOKED"} onClick={onRevoke}>Revoke</button></div>
    </article>
  );
}

function DownloadPage({ fileId }: { fileId: string }) {
  const token = window.location.hash.slice(1);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [file, setFile] = useState<DropJetFile | null>(null);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [error, setError] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setError("This link is missing its secret token.");
      setState("error");
      return;
    }
    void resolveShare(fileId, token)
      .then((result) => {
        setFile(result.file);
        setDownloadUrl(result.downloadUrl);
        setState("ready");
      })
      .catch((caught: unknown) => {
        setError(errorMessage(caught));
        setState("error");
      });
  }, [fileId, token]);

  const remaining = useMemo(() => file ? Math.max(0, file.expiresAt - Math.floor(Date.now() / 1000)) : 0, [file]);
  return (
    <Shell>
      <section className="download-wrap">
        <div className="card download-card">
          <div className="download-orbit"><span>↓</span></div>
          {state === "loading" && <><p className="eyebrow">Incoming drop</p><h1>Checking the flight manifest…</h1></>}
          {state === "error" && <><p className="eyebrow error-text">Unavailable</p><h1>This drop cannot be retrieved.</h1><p>{error}</p><a className="button secondary" href="/">Go to DropJet</a></>}
          {state === "ready" && file && <><p className="eyebrow success">Cleared for download</p><h1>{file.originalName}</h1><p>{formatBytes(file.size)} · link has roughly {Math.ceil(remaining / 3600)} hour(s) remaining</p><a className="button primary" href={downloadUrl}>Download file ↓</a><small>The generated download URL is valid for only 60 seconds.</small></>}
        </div>
      </section>
    </Shell>
  );
}
